import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CreditCardIcon, BuildingLibraryIcon, CalendarDaysIcon, ArrowTopRightOnSquareIcon, CheckIcon, ExclamationTriangleIcon, DocumentTextIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { billingAPI } from '../../services/api'
import { t, formatDate } from '../../i18n'
import { planServicesText, planUsersText, isHeading } from '../../config/plans'

// The business's own subscription to the platform: plan, next payment,
// the card or bank account Stripe charges every month, and past invoices.
const STATUS = {
  trialing: { label: 'Free trial', tone: 'bg-sky-100 text-sky-800' },
  active: { label: 'Active', tone: 'bg-emerald-100 text-emerald-800' },
  past_due: { label: 'Payment overdue', tone: 'bg-amber-100 text-amber-800' },
  unpaid: { label: 'Unpaid', tone: 'bg-red-100 text-red-700' },
  incomplete: { label: 'Waiting for payment', tone: 'bg-amber-100 text-amber-800' },
  canceled: { label: 'Cancelled', tone: 'bg-gray-100 text-gray-600' },
}
const INVOICE_STATUS = { paid: 'Paid', open: 'Due', void: 'Void', uncollectible: 'Failed', draft: 'Draft' }

const money = p => (p ? `${p.amount} ${p.currency}` : '—')
const brandName = b => (b ? b.charAt(0).toUpperCase() + b.slice(1) : t('Card'))

function PaymentMethod({ method }) {
  if (!method) return <p className="text-sm text-gray-500">{t('No card or bank account linked yet.')}</p>
  if (method.type === 'bank') {
    return (
      <div className="flex items-center gap-3">
        <span className="w-12 h-8 rounded-md bg-slate-800 text-white grid place-items-center"><BuildingLibraryIcon className="h-5 w-5" /></span>
        <div>
          <p className="font-medium text-gray-900">{method.bankName || t('Bank account')} •••• {method.last4}</p>
          <p className="text-xs text-gray-500">{t('Bank account linked via Stripe')}</p>
        </div>
      </div>
    )
  }
  if (method.type === 'card') {
    return (
      <div className="flex items-center gap-3">
        <span className="w-12 h-8 rounded-md bg-gradient-to-br from-indigo-600 to-slate-800 text-white grid place-items-center"><CreditCardIcon className="h-5 w-5" /></span>
        <div>
          <p className="font-medium text-gray-900">{brandName(method.brand)} •••• {method.last4}</p>
          <p className="text-xs text-gray-500">{t('Expires {date}', { date: `${String(method.expMonth).padStart(2, '0')}/${method.expYear}` })}</p>
        </div>
      </div>
    )
  }
  return <p className="text-sm text-gray-700">{method.type}</p>
}

export default function BillingPage({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [planId, setPlanId] = useState(null)

  const load = () => {
    setLoading(true)
    return billingAPI.getMyStatus()
      .then(({ data }) => { setStatus(data); setPlanId(p => p || data.planId || data.plans?.[0]?.id || null) })
      .catch(err => toast.error(t(err.response?.data?.error || 'Could not load billing')))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    const result = searchParams.get('billing')
    if (result === 'success') toast.success(t('Subscription started. Thank you!'))
    if (result === 'cancelled') toast(t('Checkout cancelled - nothing was charged.'))
    if (result) { searchParams.delete('billing'); setSearchParams(searchParams, { replace: true }) }
  }, [])

  const openPortal = async () => {
    setBusy('portal')
    try {
      const { data } = await billingAPI.getMyPortal()
      window.location.href = data.url
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Billing portal is not available yet'))
      setBusy('')
    }
  }

  const subscribe = async () => {
    if (!planId) return toast.error(t('Choose a plan first'))
    setBusy('subscribe')
    try {
      const { data } = await billingAPI.subscribe(planId)
      window.location.href = data.url
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Could not start checkout'))
      setBusy('')
    }
  }

  if (loading && !status) return <div className="card"><div className="card-body text-gray-500">{t('Loading billing...')}</div></div>
  if (!status) return null

  const state = STATUS[status.subscriptionStatus] || { label: 'No subscription', tone: 'bg-gray-100 text-gray-600' }
  const subscribed = status.hasSubscription && status.subscriptionStatus !== 'canceled'
  const trialDaysLeft = status.subscriptionStatus === 'trialing' && status.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(status.trialEndsAt) - Date.now()) / 86400000)) : null

  return (
    <div className={`space-y-6 ${embedded ? '' : 'max-w-5xl'}`}>
      {!embedded && (
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('Billing')}</h1>
            <p className="text-gray-600">{t('Your monthly subscription, payment method and invoices.')}</p>
          </div>
          <button onClick={load} className="btn-secondary inline-flex items-center gap-2" disabled={loading}>
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />{t('Refresh')}
          </button>
        </div>
      )}

      {status.subscriptionStatus === 'past_due' && (
        <div className="flex gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm">{t("Your last payment didn't go through. Update your payment method to keep your business online.")}</p>
        </div>
      )}
      {status.cancelAtPeriodEnd && (
        <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700">
          {t('Your subscription ends on {date} and will not renew.', { date: formatDate(status.currentPeriodEnd) })}
        </div>
      )}
      {status.liveError && <p className="text-sm text-amber-700">{t(status.liveError)}</p>}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Plan */}
        <section className="card lg:col-span-2">
          <div className="card-body">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Current plan')}</p>
                <h2 className="text-2xl font-bold text-gray-900 mt-1">{status.planName ? t(status.planName) : t('No plan yet')}</h2>
                <span className={`inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-semibold ${state.tone}`}>{t(state.label)}</span>
              </div>
              {status.price && (
                <p className="text-3xl font-bold text-gray-900">{money(status.price)}<span className="text-sm font-normal text-gray-500"> / {t('month')}</span></p>
              )}
            </div>

            <dl className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="rounded-xl bg-gray-50 p-4">
                <dt className="flex items-center gap-2 text-sm text-gray-500"><CalendarDaysIcon className="h-4 w-4" />{t('Next payment')}</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  {status.nextPayment && !status.cancelAtPeriodEnd ? formatDate(status.nextPayment.date, { dateStyle: 'long' }) : '—'}
                </dd>
                {status.nextPayment?.amount && !status.cancelAtPeriodEnd && <p className="text-sm text-gray-600">{t('{amount} will be charged', { amount: money(status.nextPayment.amount) })}</p>}
              </div>
              <div className="rounded-xl bg-gray-50 p-4">
                <dt className="text-sm text-gray-500">{trialDaysLeft != null ? t('Free trial') : t('Current period ends')}</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  {trialDaysLeft != null ? t('{count} days left', { count: trialDaysLeft }) : (status.currentPeriodEnd ? formatDate(status.currentPeriodEnd, { dateStyle: 'long' }) : '—')}
                </dd>
                <p className="text-sm text-gray-600">{t('Billed monthly')}</p>
              </div>
            </dl>
          </div>
        </section>

        {/* Payment method */}
        <section className="card">
          <div className="card-body flex flex-col h-full">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-4">{t('Payment method')}</p>
            <PaymentMethod method={status.paymentMethod} />
            <p className="text-xs text-gray-500 mt-4">{t('Pay by card or link your bank account securely with Stripe. We never see your full card or account number.')}</p>
            <div className="mt-auto pt-5">
              {status.hasBillingAccount ? (
                <button onClick={openPortal} disabled={!!busy} className="btn-primary w-full inline-flex items-center justify-center gap-2">
                  {busy === 'portal' ? t('Opening...') : t('Manage payment method')}<ArrowTopRightOnSquareIcon className="h-4 w-4 rtl-flip" />
                </button>
              ) : (
                <p className="text-sm text-gray-600">{t('Subscribe below to add a card or bank account.')}</p>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Choose a plan */}
      {!subscribed && (
        <section className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">{t('Choose your monthly plan')}</h2>
            <p className="text-sm text-gray-600">{t('Cancel anytime. You pay the same amount every month by card or bank debit.')}</p>
          </div>
          <div className="card-body">
            {!status.stripeConfigured && <p className="mb-4 text-sm rounded-lg bg-amber-50 text-amber-800 px-3 py-2">{t('Online payments are not set up on this platform yet. Contact support to activate your subscription.')}</p>}
            {status.plans.length === 0 ? (
              <p className="text-gray-500">{t('No plans are available yet. Contact support.')}</p>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {status.plans.map(plan => {
                  // A plan with fewer services than the store runs cannot be bought.
                  const tooSmall = plan.max_services != null && status.servicesCount > plan.max_services
                  return (
                  <button key={plan.id} type="button" onClick={() => setPlanId(plan.id)} aria-pressed={planId === plan.id} disabled={tooSmall}
                    className={`text-start rounded-xl border-2 p-5 transition disabled:cursor-not-allowed disabled:opacity-50 ${planId === plan.id ? 'border-emerald-600 bg-emerald-50/50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">{t(plan.name)}</h3>
                      {planId === plan.id && <CheckIcon className="h-5 w-5 text-emerald-600" />}
                    </div>
                    {plan.id === status.planId && <span className="mt-1 inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">{t('Your chosen plan')}</span>}
                    <p className="mt-2 text-2xl font-bold text-gray-900">{money(plan.price)}<span className="text-sm font-normal text-gray-500"> / {t('month')}</span></p>
                    <p className="mt-2 text-sm font-medium text-gray-800">{planServicesText(plan)}</p>
                    <p className="text-sm text-gray-600">{planUsersText(plan)}</p>
                    {tooSmall && <p className="mt-2 text-xs text-amber-700">{t('Your store runs more services than this plan includes.')}</p>}
                    {plan.features?.length > 0 && (
                      <ul className="mt-3 space-y-1 text-sm text-gray-600">
                        {plan.features.map(f => isHeading(f) ? <li key={f} className="pt-1 text-xs font-semibold text-gray-500">{t(f)}</li> : <li key={f} className="flex gap-2"><CheckIcon className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />{t(f)}</li>)}
                      </ul>
                    )}
                  </button>
                  )
                })}
              </div>
            )}
            <button onClick={subscribe} disabled={!!busy || !planId || !status.stripeConfigured} className="btn-primary mt-5 disabled:opacity-50">
              {busy === 'subscribe' ? t('Redirecting to Stripe...') : t('Subscribe monthly')}
            </button>
          </div>
        </section>
      )}

      {/* Invoices */}
      <section className="card">
        <div className="card-header"><h2 className="text-lg font-semibold text-gray-900">{t('Invoices')}</h2></div>
        <div className="card-body p-0">
          {status.invoices.length === 0 ? (
            <p className="p-6 text-gray-500 text-sm">{t('No invoices yet.')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-6 py-3 text-start font-medium">{t('Date')}</th>
                    <th className="px-6 py-3 text-start font-medium">{t('Period')}</th>
                    <th className="px-6 py-3 text-start font-medium">{t('Amount')}</th>
                    <th className="px-6 py-3 text-start font-medium">{t('Status')}</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {status.invoices.map(inv => (
                    <tr key={inv.id}>
                      <td className="px-6 py-3 whitespace-nowrap">{formatDate(inv.createdAt)}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-gray-600">{inv.periodStart ? `${formatDate(inv.periodStart)} – ${formatDate(inv.periodEnd)}` : '—'}</td>
                      <td className="px-6 py-3 whitespace-nowrap font-medium">{money(inv.display)}</td>
                      <td className="px-6 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{t(INVOICE_STATUS[inv.status] || inv.status || '')}</span></td>
                      <td className="px-6 py-3 text-end">{inv.url && <a href={inv.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:underline"><DocumentTextIcon className="h-4 w-4" />{t('View')}</a>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
